// Vendor libraries
import { SimpleChanges } from '@angular/core';
import scrollIntoView from 'scroll-into-view-if-needed';
import uuid from 'uuid-random';
// Pipes
import { GetCategoryTopScrollIdPipe } from '../../pipes/get-category-top-scroll-id.pipe';
import { GetMetricScrollIdPipe } from '../../pipes/get-metric-scroll-id.pipe';
import { GetCategoryScrollIdPipe } from '../../pipes/get-category-scroll-id.pipe';
import { MetricInfoModalComponent } from 'CalculationsApp/ts/features/metric-selector-modal/components/metric-info-modal/metric-info-modal.component';
import * as i0 from "@angular/core";
import * as i1 from "../../pipes/get-category-scroll-id.pipe";
import * as i2 from "../../pipes/get-category-top-scroll-id.pipe";
import * as i3 from "../../pipes/get-metric-scroll-id.pipe";
/**
 * Metric By Category List
 *
 * A component that displays metrics in two columns.
 *   Left column: Categories
 *   Right column: Metrics of a selected category
 * This component should not be used directly. Instead, it should be extended by another component that implements
 * `selectMetric`.
 *
 * Inputs:
 *   categories: A list of metric category objects containing their labels and metrics organized by sections.
 *   showInfoPopover: Whether to show info popover for individual items.
 *   fetchCategories: Whether to call staticMenuService to fetch categories.
 *
 *
 * Notes:
 *   Categories are displayed in the left 'Categories' column. Sections of the currently selected category are displayed
 *   in the right 'Metrics' column. Items in these sections can be displayed as individual metrics, or if the items are
 *   submenus, they are displayed as dropdowns containing metrics.
 *
 */
export class MetricByCategoryListDirective {
    constructor(getCategoryScrollId, getCategoryTopScrollId, getMetricScrollId) {
        this.getCategoryScrollId = getCategoryScrollId;
        this.getCategoryTopScrollId = getCategoryTopScrollId;
        this.getMetricScrollId = getMetricScrollId;
        this.categories = [];
        this.fetchCategories = true;
        this.showInfoPopover = true;
        /**
         * A string that uniquely identifies a component that extends the MetricByCategoryListDirective.
         * This is used for scrolling to the correct element where there are multiple components extending this directive
         * on the same page.
         */
        this.metricByCategoryListId = uuid();
        this.categorySelected = null;
        /**
         * Map for displaying the number of metrics selected per category.
         *
         * Usage: Leave this map empty if you do not want to display the number selected per category
         */
        this.numSelectedPerCategoryMap = {};
        /**
         * Map for displaying checkmarks next to selected metrics and sections containing selected metrics.
         *
         * Usage:
         *   this.selectedMetricsMap[selectedCategory.category_label][section.section_label][section.items[i].internal_name]
         *   this.selectedMetricsMap[[selectedCategory.category_label]section.section_label][section.items[i].menu_label][section.items[i].periods[j].internal_name]
         *
         * Note:
         *   A deeply nested composite object is used to uniquely identify metrics and quickly check whether a metric is
         *   selected.
         */
        this.selectedMetricsMap = {};
        this.flatSelectedMetricsMap = {};
        this.selectedMetricsList = [];
        /**
         * A map object that is *almost* the same as a flattened selectedMetricsMap. Unlike selectedMetricsMap, metrics are
         * not repeated; the last occurence of the metric in selectedMetricsMap is saved. The map is used to find a menu
         * location of a metric returned by the autocompleter.
         */
        this.flatMetricsMap = {};
        /**
         * Map for keeping opened submenus open as the user clicks on different metric categories.
         *
         * Usage: this.submenuIsOpenMap[section.section_label][section.items[i].menu_label]
         */
        this.submenuIsOpenMap = {};
    }
    ngOnChanges(changes) {
        if (changes?.categories) {
            this.reset();
        }
    }
    /**
     * Helpers
     */
    /**
     * Build all map objects needed for this component.
     * These maps are: numSelectedPerCategoryMap, selectedMetricsMap, submenuIsOpenMap
     */
    buildMaps() {
        const newNumSelectedPerCategoryMap = {};
        const newSelectedMetricsMap = {};
        const newSelectedMetricsMapFlat = {};
        const newSubmenuIsOpenMap = {};
        // TODO: Build numSelectedPerCategoryMap for multi select metric modal
        this.categories.forEach((category) => {
            newSelectedMetricsMap[category.category_label] = {};
            category.sections.forEach((section) => {
                newSelectedMetricsMap[category.category_label][section.section_label] = {};
                newNumSelectedPerCategoryMap[category.category_label] = 0;
                section.items.forEach((item) => {
                    // For sections that contain submenus, loop through all the menus.
                    // Set each submenu to be closed by default.
                    // The metrics under submenus are all different period metrics.
                    // Set all period metrics to false, meaning not selected by default.
                    if (item?.menu_label) {
                        // TODO: Add category to this map?
                        newSubmenuIsOpenMap[section.section_label] = { [item.menu_label]: false };
                        newSelectedMetricsMap[category.category_label][section.section_label][item.menu_label] = {};
                        item.periods.forEach((period) => {
                            newSelectedMetricsMap[category.category_label][section.section_label][item.menu_label][period.internal_name] = false;
                            newSelectedMetricsMapFlat[period.internal_name] = {
                                id: period.internal_name,
                                categoryLabel: category.category_label,
                                sectionLabel: section.section_label,
                                menuLabel: item.menu_label,
                                metric: period,
                            };
                        });
                    }
                    // For sections that do not contain submenus, set all metrics in each section to false,
                    // meaning not selected by default.
                    else {
                        newSelectedMetricsMap[category.category_label][section.section_label][item.internal_name] = false;
                        newSelectedMetricsMapFlat[item.internal_name] = {
                            id: item.internal_name,
                            categoryLabel: category.category_label,
                            sectionLabel: section.section_label,
                            metric: item,
                        };
                    }
                });
            });
        });
        // Assign new map values
        this.numSelectedPerCategoryMap = newNumSelectedPerCategoryMap;
        this.selectedMetricsMap = newSelectedMetricsMap;
        this.flatMetricsMap = newSelectedMetricsMapFlat;
        this.submenuIsOpenMap = newSubmenuIsOpenMap;
    }
    /**
     * Reset the metrics menus displayed.
     */
    reset() {
        if (this.categories.length > 0) {
            const [firstCategory] = this.categories;
            this.categorySelected = firstCategory;
        }
        else {
            this.categorySelected = null;
        }
        this.numSelectedPerCategoryMap = {};
        this.selectedMetricsMap = {};
        this.flatSelectedMetricsMap = {};
        this.selectedMetricsList = [];
        this.flatMetricsMap = {};
        this.submenuIsOpenMap = {};
        this.buildMaps();
    }
    /**
     * Select
     */
    /**
     * Select the category in the left column to change metrics displayed in the right column.
     * @param category: The selected category in the left column.
     */
    selectCategory(category) {
        this.categorySelected = category;
        this.scrollToCategoryTop(category.category_label);
    }
    /**
     * Update all relevant maps to include the selection of a new metric
     *
     * Called when selecting a metric from the multiMetricSelectorModal or when
     * initializing selected metric @input for the singleMetricSelectorModal
     *
     * Update the selectedMetricMap so a checkmark is displayed for the new metric
     * Update the flatSelectedMetricsMap to include an entry for the new metric
     * update the selectedMetricsList to include an entry for the new metric
     * Update the numSelectedPerCategoryMap to account for the new metric
     *
     * @param metricInfo: info
     * @param categoryLabel: the new metric's category label
     * @param sectionLabel: the new metric's section label
     * @param menuLabel?: the new metric's menu label if applicable
     */
    addSelectedMetricToMaps(metricInfo) {
        const { metric, categoryLabel, sectionLabel, menuLabel } = metricInfo;
        // Update the selectedMetricMap and numSelectedPerCategoryMap
        const newSelectedMetricsMap = { ...this.selectedMetricsMap };
        if (menuLabel) {
            const currentValue = newSelectedMetricsMap[categoryLabel][sectionLabel][menuLabel][metric.internal_name];
            newSelectedMetricsMap[categoryLabel][sectionLabel][menuLabel][metric.internal_name] = !currentValue;
            this.numSelectedPerCategoryMap[categoryLabel] += currentValue ? -1 : 1;
        }
        else {
            const currentValue = newSelectedMetricsMap[categoryLabel][sectionLabel][metric.internal_name];
            newSelectedMetricsMap[categoryLabel][sectionLabel][metric.internal_name] =
                !currentValue;
            this.numSelectedPerCategoryMap[categoryLabel] += currentValue ? -1 : 1;
        }
        // Create a new map object so Angular can detect changes for the composite object `selectedMetricsMap`
        this.selectedMetricsMap = newSelectedMetricsMap;
        // Update the flatSelectedMetricsMap and selectedMetricsList
        if (this.flatSelectedMetricsMap[metric.internal_name]) {
            const { [metric.internal_name]: _, ...newFlatSelectedMetricsMap } = this.flatSelectedMetricsMap;
            this.flatSelectedMetricsMap = newFlatSelectedMetricsMap;
        }
        else {
            this.flatSelectedMetricsMap = {
                ...this.flatSelectedMetricsMap,
                [metric.internal_name]: metric,
            };
        }
        this.selectedMetricsList = Object.values(this.flatSelectedMetricsMap);
    }
    /**
     * Open / Close
     */
    /**
     * Open or close the clicked toggle button's submenu.
     * @param sectionLabel: The section of the clicked menu.
     * @param menuLabel: The clicked menu of a section in the selected category.
     */
    toggleSubmenu(sectionLabel, menuLabel) {
        this.submenuIsOpenMap[sectionLabel][menuLabel] = !this.submenuIsOpenMap[sectionLabel][menuLabel];
    }
    /**
     * Open info modal for metric when user clicks on a metric 'info' icon.
     * Stops event from propagating to parent div, which prevents metric from being selected when 'info' is clicked
     */
    openMetricInfoModal(item, event) {
        this.metricInfoModal.openModal(item);
        event.stopPropagation();
    }
    /**
     * Scroll to a metric in the right side column 'Metrics'.
     * @param metricMenuItemInfo: An object of labels to locate a specific metric in the menu.
     */
    scrollToMetric(metricMenuItemInfo) {
        const { id, categoryLabel, sectionLabel, menuLabel } = metricMenuItemInfo;
        // Select the category in the left column
        this.categorySelected =
            this.categories.find((category) => category.category_label === categoryLabel) ?? null;
        // Scroll to the selected category
        const categoryScrollId = this.getCategoryScrollId.transform(this.metricByCategoryListId, categoryLabel);
        const categoryCssSelectors = `div.linked-list-item-header[category-scroll-id="${categoryScrollId}"]`;
        this.scrollToIfNeeded(categoryCssSelectors, { block: 'center' });
        // If the selected item is in a submenu then open the submenu so the metric is visible
        if (metricMenuItemInfo?.menuLabel) {
            this.submenuIsOpenMap[sectionLabel][menuLabel] = true;
        }
        // Scroll to the selected metric
        const metricScrollId = this.getMetricScrollId.transform(this.metricByCategoryListId, id, categoryLabel, sectionLabel, menuLabel ?? '');
        const divClass = metricMenuItemInfo?.menuLabel ? '.linked-list-item' : '.linked-list-item-header';
        const metricScrollCssSelectors = `div${divClass}[metric-scroll-id="${metricScrollId}"]`;
        this.scrollToIfNeeded(metricScrollCssSelectors, { block: 'center' });
    }
    /**
     * Scroll to the top of the right side column 'Metrics'.
     * @param categoryLabel: The label of a category.
     */
    scrollToCategoryTop(categoryLabel) {
        const categoryTopScrollId = this.getCategoryTopScrollId.transform(this.metricByCategoryListId, categoryLabel);
        const cssSelectors = `span[category-top-scroll-id="${categoryTopScrollId}"]`;
        this.scrollToIfNeeded(cssSelectors);
    }
    /**
     * Scroll to the element with the given css selectors if an element is found, and that element is not already
     * visible.
     * @param cssSelectors: A string containing css selectors. Ex: 'div.myClassName[attributeName='attrValue']'
     * @param scrollIntoViewOptions: An object of scrolling options from the scroll-into-view library. Use this to
     *   specify where the scrolled item should display. Ex: { block: 'center' }. By default, scrolled to items are
     *   displayed at the top of the parent.
     */
    scrollToIfNeeded(cssSelectors, scrollIntoViewOptions = {}) {
        setTimeout(() => {
            const elem = document.querySelector(cssSelectors);
            const _scrollIntoViewOptions = {
                ...scrollIntoViewOptions, // do not scroll beyond the modal
                boundary: (parent) => !parent.classList.contains('modal'),
                scrollMode: 'if-needed',
            };
            if (elem) {
                scrollIntoView(elem, _scrollIntoViewOptions);
            }
        });
    }
    /**
     * Get the category label, section label, and menu label (if the metric is part of a submenu) of a metric.
     * @param metricId: The string indentifying a metric, also known as the Metric.internal_name or
     * AutocompleteResult.search_id.
     */
    getMetricInfo(metricId) {
        return this.flatMetricsMap[metricId];
    }
}
MetricByCategoryListDirective.ɵfac = function MetricByCategoryListDirective_Factory(t) { return new (t || MetricByCategoryListDirective)(i0.ɵɵdirectiveInject(i1.GetCategoryScrollIdPipe), i0.ɵɵdirectiveInject(i2.GetCategoryTopScrollIdPipe), i0.ɵɵdirectiveInject(i3.GetMetricScrollIdPipe)); };
MetricByCategoryListDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: MetricByCategoryListDirective, viewQuery: function MetricByCategoryListDirective_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(MetricInfoModalComponent, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.metricInfoModal = _t.first);
    } }, inputs: { categories: "categories", fetchCategories: "fetchCategories", showInfoPopover: "showInfoPopover" }, features: [i0.ɵɵNgOnChangesFeature] });
